import { useQuery, useMutation, useQueryClient, QueryKey } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { crudFactory } from '../genericCrudFactory';
import { ApiResponse } from 'http-client/types/auth.types';
import { getMessageByKey } from 'http-client/mappings/appMessagesMapping';
import { buildUrlWithParams, isValidParams } from 'helpers/url_helper';
import { HttpClient } from '../apiClient';
import { API_ENDPOINTS } from '../httpConstants';

export const useListQuery = <T>(url: string, params: Record<string, any> = {}) => {
  const buildUrl = buildUrlWithParams(url, params ?? {});
  const client = crudFactory<T>(buildUrl);

  return useQuery({
    queryKey: [url, params] as QueryKey,
    queryFn: () => client.findAll(params),
    select: (response) => {
      if (!response.success) {
        console.error('API Error:', response.message || response.errors);
        return [];
      }

      return response.data ?? [];
    },
  });
};

/**
 * Hook for paginated list queries (replacement for useListQuery with pagination params)
 * Handles: pageNumber, pageSize, search, sortBy, sortDirection, and custom filters
 */
export const usePaginatedListQuery = <T>(
  url: string,
  params: {
    pageNumber?: number;
    pageSize?: number;
    search?: string;
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
    [key: string]: any;
  } = {},
  options: {
    enabled?: boolean;
  } = {},
) => {
  return useQuery({
    queryKey: [url, params] as QueryKey,
    queryFn: async () => {
      const response = await HttpClient.get<ApiResponse<any>>(url, {
        params: {
          pageNumber: params.pageNumber ?? 1,
          pageSize: params.pageSize ?? 20,
          ...(params.search && { search: params.search }),
          ...(params.sortBy && { sortBy: params.sortBy }),
          ...(params.sortDirection && { sortDirection: params.sortDirection }),
          // Pass through any other filter parameters
          ...Object.fromEntries(
            Object.entries(params).filter(
              ([key]) =>
                !['pageNumber', 'pageSize', 'search', 'sortBy', 'sortDirection'].includes(key),
            ),
          ),
        },
      });
      return response;
    },
    select: (response) => {
      if (!response?.success) {
        console.error('API Error:', response?.message || response?.errors);
        return {
          items: [],
          pageNumber: 1,
          pageSize: 20,
          totalCount: 0,
          totalPages: 0,
          hasNextPage: false,
          hasPreviousPage: false,
        };
      }

      return (
        response.data ?? {
          items: [],
          pageNumber: 1,
          pageSize: 20,
          totalCount: 0,
          totalPages: 0,
          hasNextPage: false,
          hasPreviousPage: false,
        }
      );
    },
    enabled: options.enabled ?? true,
  });
};

export const useDetailQuery = <T>(url: string, params: Record<string, string | undefined> = {}) => {
  const client = crudFactory<T>(url);
  return useQuery({
    queryKey: [url, params] as QueryKey,
    queryFn: () => client.findOne(params),
    select: (response) => {
      if (!response.success) {
        console.error('API Error:', response.message || response.errors);
        return null;
      }
      return response.data ?? null;
    },
    enabled: isValidParams(params),
  });
};

export const useCreateMutation = <T, CreateDto = Partial<T>>(url: string, queryKey?: string) => {
  const client = crudFactory<T, CreateDto>(url);
  const queryClient = useQueryClient();

  return useMutation<ApiResponse<T>, unknown, CreateDto>({
    mutationFn: (data: CreateDto) => client.create(data),
    onSuccess: (response) => {
      if (response?.message && response?.statusCode >= 200 && response?.statusCode < 300) {
        toast.success(getMessageByKey(response.message));
      } else {
        toast.error(getMessageByKey(response.message || '') || 'An error occurred.');
      }
      queryClient.invalidateQueries({ queryKey: [queryKey ?? url] });
    },
    onError: (error: any) => {
      toast.error(getMessageByKey(error?.response?.data?.message || '') || 'An error occurred.');
    },
  });
};

export const useUpdateMutation = <T, UpdateDto = Partial<T>>(url: string, queryKey?: string) => {
  const client = crudFactory<T, any, UpdateDto>(url);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: UpdateDto }) => client.update(id, data),
    onSuccess: () => {
      toast.success(`${url} updated successfully`);
      queryClient.invalidateQueries({ queryKey: [queryKey ?? url] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Something went wrong.');
    },
  });
};

export const useUpdatePatchMutation = <T, UpdateDto = Partial<T>>(
  url: string,
  queryKey?: string,
) => {
  const client = crudFactory<T, any, UpdateDto>(url);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data, params }: { data: UpdateDto; params: Record<string, any> }) =>
      client.updatePatch(data, params),
    onSuccess: (response) => {
      if (response?.message && response?.statusCode >= 200 && response?.statusCode < 300) {
        toast.success(getMessageByKey(response.message));
      } else {
        toast.error(getMessageByKey(response.message || '') || 'An error occurred.');
      }
      queryClient.invalidateQueries({ queryKey: [queryKey ?? url] });
    },
    onError: (error: any) => {
      toast.error(getMessageByKey(error?.response?.data?.message || '') || 'An error occurred.');
    },
  });
};

interface UploadImageResponseData {
  url?: string;
}

export const useUploadImageMutation = (uploadUrl?: string, invalidateQueryKey?: QueryKey) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      shopId,
      file,
      imageType,
    }: {
      shopId: string;
      file: File;
      imageType: string;
    }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('imageType', imageType);

      const url = uploadUrl
        ? `${uploadUrl}?imageType=${imageType}`
        : `${API_ENDPOINTS.TENANT.SHOPS.UPLOAD_IMAGE(shopId)}?imageType=${imageType}`;

      return HttpClient.post<ApiResponse<UploadImageResponseData>>(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    onSuccess: (response, variables) => {
      toast.success(getMessageByKey(response.message || '') || 'Image uploaded successfully.');
      if (invalidateQueryKey) {
        queryClient.invalidateQueries({ queryKey: invalidateQueryKey });
      } else {
        queryClient.invalidateQueries({
          queryKey: [API_ENDPOINTS.TENANT.SHOPS.WITH_ID, { id: variables.shopId }],
        });
      }
    },
    onError: (error: unknown) => {
      const axiosError = error as { response?: { data?: { message?: string } } };
      toast.error(
        getMessageByKey(axiosError?.response?.data?.message || '') || 'Failed to upload image.',
      );
    },
  });
};

export const useDeleteMutation = <T>(url: string, queryKey?: string) => {
  const client = crudFactory<T>(url);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string | number) => client.remove(id),
    onSuccess: () => {
      toast.success(`Operation Successful`);
      queryClient.invalidateQueries({ queryKey: [queryKey ?? url] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Something went wrong.');
    },
  });
};

const invalidateKeys = (
  queryClient: ReturnType<typeof useQueryClient>,
  keys?: QueryKey[],
) => {
  keys?.forEach((key) => queryClient.invalidateQueries({ queryKey: key }));
};

const handleMutationResponse = (response?: ApiResponse<unknown>) => {
  if (response?.message && response?.statusCode >= 200 && response?.statusCode < 300) {
    toast.success(getMessageByKey(response.message));
  } else if (response?.message) {
    toast.error(getMessageByKey(response.message) || 'An error occurred.');
  } else {
    toast.success('Operation successful');
  }
};

/**
 * POST action on a resource sub-path (e.g. /staff/{id}/deactivate).
 */
export const usePostActionMutation = <TResponse = unknown, TBody = Record<string, never>>(
  urlFn: (id: string) => string,
  invalidateQueryKeys?: QueryKey[],
) => {
  const queryClient = useQueryClient();

  return useMutation<ApiResponse<TResponse>, unknown, { id: string; body?: TBody }>({
    mutationFn: ({ id, body }) =>
      HttpClient.post<ApiResponse<TResponse>>(urlFn(id), body ?? ({} as TBody)),
    onSuccess: (response) => {
      handleMutationResponse(response);
      invalidateKeys(queryClient, invalidateQueryKeys);
    },
    onError: (error: any) => {
      toast.error(
        getMessageByKey(error?.response?.data?.message || '') || 'Something went wrong.',
      );
    },
  });
};

/**
 * PUT action on a resource sub-path (e.g. /staff/{id}/role).
 */
export const usePutActionMutation = <TResponse = unknown, TBody = unknown>(
  urlFn: (id: string) => string,
  invalidateQueryKeys?: QueryKey[],
) => {
  const queryClient = useQueryClient();

  return useMutation<ApiResponse<TResponse>, unknown, { id: string; body: TBody }>({
    mutationFn: ({ id, body }) => HttpClient.put<ApiResponse<TResponse>>(urlFn(id), body),
    onSuccess: (response) => {
      handleMutationResponse(response);
      invalidateKeys(queryClient, invalidateQueryKeys);
    },
    onError: (error: any) => {
      toast.error(
        getMessageByKey(error?.response?.data?.message || '') || 'Something went wrong.',
      );
    },
  });
};
